/**
 * Vessel tuning data (IMPLEMENTATION_PLAN.md A3).
 *
 * Data only — no engine, no DOM, no behaviour. Both the simulation and the renderer read
 * from here so a hull's physical size, its buoyancy baseline and its collision box can
 * never disagree with what is drawn.
 */

import type { VesselKind, Faction } from '../sim/types';
import {
  KRAKEN_VISUAL_BEAM,
  KRAKEN_VISUAL_HEIGHT,
  KRAKEN_VISUAL_LENGTH,
  TENTACLE_BOUND_RADIUS,
  TENTACLE_ENVELOPE,
  TENTACLE_HIT_SPHERES,
  type HitSphere,
} from './creatureScale';

export type ArmourClass = 'soft' | 'light' | 'armoured';

/**
 * Fraction of a hostile round's damage the rig takes when it splashes inside the
 * platform's 55 m ring (see `stepProjectilesAndDamage`).
 *
 * Tuned by measurement, not taste: at the old 0.1 a single unanswered gunboat cost
 * 26 integrity a minute — a pair was a two-minute defeat. At 0.02 the same gunboat
 * costs ~5 a minute: an alarm the player must answer inside a minute or two, priced so
 * that ignoring one attacker stings and ignoring a pair genuinely hurts, while neither
 * is a sudden loss.
 */
export const RIG_SPLASH_DAMAGE_FACTOR = 0.02;

export interface VesselSpec {
  kind: VesselKind;
  /**
   * An explicit hit volume in the body's own local frame, replacing the centreline
   * capsule.
   *
   * The capsule is a good description of anything long and floating and a bad one of a
   * body that stands up out of the sea and leans as it rises. Only the tentacle carries
   * this; every hull is better served by the cheaper capsule.
   */
  hitSpheres?: readonly HitSphere[];
  /** Radius that encloses `hitSpheres` from the origin — the broad-phase reject. */
  hitBoundRadius?: number;
  /**
   * This body is deformed every frame, and the hit volume has to follow.
   *
   * Names the motion the simulation should evaluate so the volume moves with the mesh
   * rather than staying in the pose the body was authored in. See
   * `content/tentacleMotion.ts` — one description, read by the renderer and the gun.
   */
  animated?: 'tentacle';
  faction: Faction;
  /** Display name for the scope's target-data panel. */
  label: string;
  /** Metres per second. */
  speed: number;
  hp: number;
  armour: ArmourClass;

  /** Hull dimensions in metres — the renderer builds to these, the sim collides on them. */
  length: number;
  beam: number;
  /** Height of the highest structure above the waterline; sets the collision box top. */
  height: number;
  /** How deep the hull sits; the render model is offset down by this. */
  draft: number;

  /** Turn rate, radians per second. Big hulls commit to their heading. */
  turnRate: number;

  /** Range at which this vessel's own weapon opens up, metres. 0 = unarmed. */
  weaponRange: number;
  /** Damage per hit against vessels. */
  weaponDamage: number;
  /** Seconds between shots/bursts. */
  weaponInterval: number;

  /**
   * Range at which the player can first see a silhouette in clear daylight, metres.
   * Scaled down by fog and darkness at runtime (plan E6).
   */
  silhouetteRange: number;
  /** How long a silhouette must be watched before it identifies, seconds. */
  identifyTime: number;

  /** Chance (0..1) of a catastrophic kill explosion rather than a normal sinking. */
  criticalChance: number;

  /**
   * Seconds the hull burns and smokes, still afloat, before it begins to settle.
   *
   * A ship does not vanish when its HP hits zero. It stops, it lists, and black smoke
   * comes up — and only then does it start going down. This phase is the whole reason a
   * kill reads as an event rather than a despawn, and it is where the player looks away
   * from the wreck and back at the water.
   */
  smokeDuration: number;

  /**
   * Seconds the hull takes to go under once it starts settling.
   *
   * Scaled hard by displacement: a skiff is gone in seconds, a laden tanker takes minutes
   * and stays in frame as a burning silhouette for most of them. Wrecks never hold a
   * shift open — the end condition ignores sinking hulls — so these can be as slow as the
   * drama wants.
   */
  sinkDuration: number;
}

/**
 * The two speedboats carry Adam's +50%, and it is REAL on every platform.
 *
 * This landed twice. The first pass put 30% in the specs and the remaining 15% on the
 * drawn mesh on touch devices only, reasoning that dimensions feed the simulation and a
 * platform-dependent hull would mean two physical worlds. The reasoning was sound and
 * the result was wrong: it inflated the silhouette without inflating the capsule, so on
 * a phone the outer 15% of a visible skiff was a guaranteed miss. Adam's point settles
 * it — **the hit area is the thing being asked for**, not the picture of it.
 *
 * So the full 50% is in the numbers below, which means the collision capsule, the
 * torpedo's proximity fuse, the buoyancy sampling and the smoke column all get it, one
 * hull size everywhere, and replay-from-seed is untouched.
 */
export const VESSEL_SPECS: Record<VesselKind, VesselSpec> = {
  tanker: {
    kind: 'tanker',
    faction: 'friendly',
    label: 'Crude Tanker',
    speed: 9.6,
    hp: 1200,
    armour: 'light',
    length: 180,
    beam: 32,
    height: 34,
    draft: 11,
    turnRate: 0.035,
    weaponRange: 0,
    weaponDamage: 0,
    weaponInterval: 0,
    silhouetteRange: 8000,
    identifyTime: 2.5,
    criticalChance: 0.35,
    smokeDuration: 30,
    sinkDuration: 150,
  },
  supply: {
    kind: 'supply',
    faction: 'friendly',
    label: 'Supply Vessel',
    speed: 12,
    hp: 600,
    armour: 'light',
    length: 78,
    beam: 16,
    height: 20,
    draft: 5.5,
    turnRate: 0.07,
    weaponRange: 0,
    weaponDamage: 0,
    weaponInterval: 0,
    silhouetteRange: 6000,
    identifyTime: 2.5,
    criticalChance: 0.2,
    smokeDuration: 18,
    sinkDuration: 72,
  },
  skiff: {
    kind: 'skiff',
    faction: 'hostile',
    label: 'Pirate Skiff',
    speed: 21,
    /*
     * Two hits, not three.
     *
     * Against soft armour the 30 mm does its full 14 a round, so skiff toughness is
     * quantised in steps of 14 and every value from 29 to 40 is the same three-hit boat.
     * The only "slightly easier" that exists here is the step down to two, which takes
     * roughly a third off the time each skiff occupies the gun. That margin matters most
     * when a pack is closing on a hull the player is meant to be protecting.
     */
    hp: 28,
    armour: 'soft',
    /*
     * Fifty per cent up on Adam's call — 9 x 2.6 was a real pirate skiff and read as a
     * dot. At a kilometre it subtended half a degree, six per cent of the 8x sight
     * picture broadside and under two per cent bow-on, so the hull the campaign leans on
     * hardest was the one the player could barely see. Accuracy lost an argument with
     * legibility, and this is the size that argument settled on.
     */
    length: 13.5,
    beam: 3.9,
    height: 3.6,
    draft: 1.05,
    turnRate: 0.9,
    weaponRange: 300,
    weaponDamage: 12,
    weaponInterval: 3.2,
    silhouetteRange: 3000,
    identifyTime: 1.5,
    criticalChance: 0.15,
    smokeDuration: 4,
    sinkDuration: 14,
  },
  gunboat: {
    kind: 'gunboat',
    faction: 'hostile',
    label: 'Pirate Gunboat',
    speed: 15,
    hp: 145,
    armour: 'light',
    length: 22,
    beam: 5.4,
    height: 6.5,
    draft: 1.6,
    turnRate: 0.42,
    weaponRange: 600,
    weaponDamage: 18,
    weaponInterval: 2.4,
    silhouetteRange: 4200,
    identifyTime: 2,
    criticalChance: 0.08,
    smokeDuration: 8,
    sinkDuration: 30,
  },
  fac: {
    kind: 'fac',
    faction: 'hostile',
    label: 'Fast Attack Craft',
    speed: 23,
    hp: 200,
    armour: 'light',
    /* Same 50% as the skiff, keeping the two speedboats in proportion to each other. */
    length: 51,
    beam: 10.5,
    height: 13.5,
    draft: 3.15,
    turnRate: 0.5,
    weaponRange: 1200,
    weaponDamage: 40,
    weaponInterval: 7,
    silhouetteRange: 5000,
    identifyTime: 2,
    criticalChance: 0.1,
    smokeDuration: 10,
    sinkDuration: 36,
  },
  minisub: {
    kind: 'minisub',
    faction: 'hostile',
    label: 'Midget Submarine',
    speed: 8,
    hp: 230,
    armour: 'light',
    length: 28,
    beam: 3.2,
    height: 3,
    draft: 2.6,
    turnRate: 0.25,
    weaponRange: 1100,
    weaponDamage: 150,
    weaponInterval: 45,
    silhouetteRange: 900,
    identifyTime: 3,
    criticalChance: 0.05,
    smokeDuration: 5,
    sinkDuration: 24,
  },
  /**
   * A tentacle of the Maw.
   *
   * Modelled as a slow hostile that closes on a friendly and crushes it: the grapple is
   * simply a very short weapon range with a fast cycle, so the existing AI and damage
   * paths carry it. The body is never spawned, because the plan is explicit that it is
   * never fully seen — the horror is entirely in the parts that reach the surface.
   */
  tentacle: {
    kind: 'tentacle',
    faction: 'hostile',
    label: 'Tentacle',
    speed: 6,
    hp: 120,
    armour: 'soft',
    /*
     * Measured off the mesh, not authored beside it.
     *
     * These were 14 × 3.2 × 12 — the placeholder the limb was prototyped from, kept while
     * the render grew into a sixty-metre arm that leans forty metres out. The gap is what
     * Adam felt as "they only take damage when you shoot them in one particular spot":
     * the hit volume covered the bottom fifth of something he could see all of.
     */
    length: TENTACLE_ENVELOPE.length,
    beam: TENTACLE_ENVELOPE.beam,
    height: TENTACLE_ENVELOPE.height,
    // What the sea hides. A killed limb withdraws through all of it rather than sinking
    // a metre and vanishing.
    draft: TENTACLE_ENVELOPE.draft,
    turnRate: 0.6,
    // Hit as the curve it is, not as the boat it is not — and as the curve is *now*,
    // not as it was authored: the volume writhes and rises with the mesh.
    hitSpheres: TENTACLE_HIT_SPHERES,
    hitBoundRadius: TENTACLE_BOUND_RADIUS,
    animated: 'tentacle',
    // Grapple range: it has to be alongside, and once it is, it is continuous.
    weaponRange: 90,
    weaponDamage: 22,
    weaponInterval: 1.6,
    silhouetteRange: 2200,
    identifyTime: 2,
    criticalChance: 0,
    smokeDuration: 0,
    sinkDuration: 6,
  },
  /**
   * The Kraken. Phase behaviour lives in the AI, not here; these are the numbers the
   * rest of the game needs to draw it, float it and shoot at it.
   */
  kraken: {
    kind: 'kraken',
    faction: 'hostile',
    label: 'Kraken',
    speed: 5,
    hp: 4200,
    armour: 'armoured',
    /*
     * The visible extent, because that is what the player is shooting at.
     *
     * These read 120 × 46 × 34 — a "combat core" a tenth of the animal, defended on the
     * grounds that a full-size capsule would detonate a torpedo hundreds of metres before
     * the mantle. That argument only holds if the mantle is small, and it is not: it is
     * five hundred and forty metres across. A fish that goes off against the flank has
     * reached the creature. Adam: "cement the ones that make the Kraken monumentally
     * huge."
     */
    length: KRAKEN_VISUAL_LENGTH,
    beam: KRAKEN_VISUAL_BEAM,
    height: KRAKEN_VISUAL_HEIGHT,
    /*
     * Shallow because the render owns how much of the much larger creature breaches.
     */
    draft: 2.5,
    turnRate: 0.10,
    weaponRange: 1400,
    weaponDamage: 90,
    weaponInterval: 11,
    silhouetteRange: 9000,
    identifyTime: 1,
    criticalChance: 0,
    smokeDuration: 0,
    sinkDuration: 40,
  },
  corvette: {
    kind: 'corvette',
    faction: 'hostile',
    label: 'Corvette',
    speed: 13,
    hp: 900,
    armour: 'armoured',
    length: 92,
    beam: 13,
    height: 22,
    draft: 4.2,
    turnRate: 0.16,
    weaponRange: 3000,
    weaponDamage: 55,
    weaponInterval: 9,
    silhouetteRange: 7000,
    identifyTime: 2.5,
    criticalChance: 0.06,
    smokeDuration: 22,
    sinkDuration: 88,
  },
};

/**
 * Weapon effectiveness against each armour class (plan E3).
 * The corvette line is the designed reason torpedoes exist: 25% autocannon effect makes
 * a stand-off gun duel unwinnable and the Shift-5 torpedo unlock pay off at Shift 9.
 */
/**
 * How deep a submerged hull runs, metres to its centre (Adam, 2026-07-29).
 *
 * These are what make the depth-charge fuse a choice. The mini-sub runs deep on the way
 * in — quiet, unreachable, which is the whole tension of her approach — and comes up to
 * a shallower circling depth once she has fired, where the projector can reach her. The
 * Kraken is the only thing in the game at 45 m, which is Adam's ruling and what the
 * deepest fuse setting exists for.
 *
 * They line up with `DEPTH_CHARGE.fuseDepths` on purpose: 25 for a boat running in, 10
 * for one circling, 45 for the creature. Matching the fuse to the target is the skill.
 */
export const SUBMERGED_DEPTHS = {
  /** Ingress, running deep and undetectable. */
  minisubDeep: 25,
  /** Circling after the attack, inside the projector's envelope. */
  minisubShallow: 10,
  /** The creature between assaults. Nothing else in the game is down here. */
  kraken: 45,
} as const;

export const ARMOUR_MATRIX: Record<string, Record<ArmourClass, number>> = {
  shell: { soft: 1, light: 0.7, armoured: 0.25 },
  shellAP: { soft: 0.8, light: 0.85, armoured: 0.6 },
  shellHE: { soft: 1.5, light: 0.8, armoured: 0.2 },
  rocket: { soft: 1, light: 1, armoured: 0.6 },
  rpg: { soft: 1, light: 1, armoured: 0.4 },
  torpedo: { soft: 1, light: 1, armoured: 1 },
  depthCharge: { soft: 1, light: 1, armoured: 0.8 },
};

export function vesselSpec(kind: VesselKind): VesselSpec {
  return VESSEL_SPECS[kind];
}
